import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  ConfigurableFocusTrapFactory,
  ConfigurableFocusTrap,
} from '@angular/cdk/a11y';
import { ButtonModule } from 'primeng/button';
import { MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { Drawer } from 'primeng/drawer';
import { LogoutService } from '@ao/app/core/services/logout.service';
import { UserAccessService } from '@ao/app/shared/services/user-access.service';
import { Subscription } from 'rxjs';
import { MenuReloadService } from '@ao/app/shared/services/menu-reload.service';
import { FeatureNames } from '@ao/shared/constants/feature-names';

@Component({
  selector: 'app-left-navigation',
  standalone: true,
  imports: [Drawer, ButtonModule, MenuModule],
  templateUrl: './left-navigation.component.html',
  styleUrls: ['./left-navigation.component.scss'],
})
export class LeftNavigationComponent implements OnInit, OnDestroy {
  @ViewChild('mainMenu') mainMenu: Menu | undefined;

  private _visible = false;
  private focusTrap: ConfigurableFocusTrap | null = null;
  private triggerElement: HTMLElement | null = null;

  @Input() set visible(value: boolean) {
    this._visible = value;
    if (!value) {
      this.destroyFocusTrap();
    }
  }
  get visible(): boolean {
    return this._visible;
  }

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() drawerClosed = new EventEmitter<void>();

  readonly logoutService = inject(LogoutService);
  private userAccessService = inject(UserAccessService);
  private menuReloadService = inject(MenuReloadService);
  private focusTrapFactory = inject(ConfigurableFocusTrapFactory);
  private reloadSubscription?: Subscription;
  menuItems: MenuItem[] | undefined;

  ngOnInit() {
    this.loadMenuItems();
    this.reloadSubscription = this.menuReloadService.reloadMenu$.subscribe(
      () => {
        this.loadMenuItems();
      }
    );
  }

  ngOnDestroy() {
    this.reloadSubscription?.unsubscribe();
    this.destroyFocusTrap();
  }
  private loadMenuItems() {
    const appsItems: MenuItem[] = [
      ...(this.userAccessService.checkIfUserHasAccessToClientOrUserManagement()
        ? [
            {
              label: 'Client Management',
              icon: 'pi pi-building-columns',
              routerLink: '/client-management',
              ariaLabel: 'Client Management menu button',
              command: () => this.closeDrawer(),
            },
            {
              label: 'User Management',
              icon: 'pi pi-users',
              routerLink: '/user-management',
              ariaLabel: 'User Management menu button',
              command: () => this.closeDrawer(),
            },
            {
              label: 'Project Management',
              icon: 'pi pi-briefcase',
              routerLink: '/project-management',
              ariaLabel: 'Project Management menu button',
              command: () => this.closeDrawer(),
            },
          ]
        : []),
      ...(this.userAccessService.checkUserRoleFeatures([FeatureNames.ADD_ROLE])
        ? [
            {
              label: 'Role Management',
              icon: 'pi pi-key',
              routerLink: '/role-management',
              ariaLabel: 'Role Management menu button',
              command: () => this.closeDrawer(),
            },
          ]
        : []),
      ...(this.userAccessService.checkIfUserHasToEscalation()
        ? [
            {
              label: 'Escalations',
              icon: 'pi pi-shield',
              routerLink: '/escalations',
              ariaLabel: 'Escalations menu button',
              command: () => this.closeDrawer(),
            },
          ]
        : []),
    ];

    this.menuItems = [
      {
        label: 'Basic',

        items: [
          {
            label: 'Home',
            icon: 'pi pi-home',
            routerLink: '/dashboard',
            ariaLabel: 'Dashboard',
            command: () => this.closeDrawer(),
          },
          {
            label: 'My Tasks',
            icon: 'pi pi-fw pi-list',
            routerLink: '/my-tasks',
            ariaLabel: 'My Tasks menu button',
            command: () => this.closeDrawer(),
          },
          {
            label: 'Help',
            icon: 'pi pi-question-circle',
            routerLink: '/help',
            ariaLabel: 'Help menu button',
            command: () => this.closeDrawer(),
          },
        ],
      },
      ...(appsItems.length > 0
        ? [
            {
              label: 'Apps',
              ariaLabel: 'Apps menu items',
              items: appsItems,
            },
          ]
        : []),
      {
        label: 'Profile',
        items: [
          {
            label: 'Logout',
            icon: 'pi pi-sign-out',
            ariaLabel: 'Logout link',
            command: () => {
              this.logout();
              this.closeDrawer();
            },
          },
        ],
      },
    ];
  }

  onDrawerShow() {
    this.onVisibleChange(true);
    this.triggerElement = document.activeElement as HTMLElement;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const menuEl = this.mainMenu?.el?.nativeElement as
          | HTMLElement
          | undefined;
        if (!menuEl) return;

        const drawerEl = menuEl.closest<HTMLElement>('.p-drawer');
        if (drawerEl) {
          drawerEl.setAttribute('aria-modal', 'true');
          this.destroyFocusTrap();
          this.focusTrap = this.focusTrapFactory.create(drawerEl);
        }
        menuEl.querySelector<HTMLElement>('a')?.focus();
      });
    });
  }

  onDrawerHide() {
    this.destroyFocusTrap();
    this.onVisibleChange(false);
    const trigger = this.triggerElement;
    this.triggerElement = null;
    requestAnimationFrame(() => trigger?.focus());
    this.drawerClosed.emit();
  }

  private destroyFocusTrap(): void {
    if (this.focusTrap) {
      this.focusTrap.destroy();
      this.focusTrap = null;
    }
  }

  onVisibleChange(event: boolean) {
    this.visibleChange.emit(event);
  }

  logout() {
    this.logoutService.logout();
  }

  closeDrawer() {
    this.visible = false;
    this.onVisibleChange(this.visible);
  }
}
